import {
  getLatestDropPhotos,
  INVENTORY_TERM,
  isInventoryOrder,
  isInventoryTerm,
  normalizeInventoryLotName
} from './inventorySelectors'

const text = value => {
  if (value === null || typeof value === 'undefined') return ''
  return String(value)
}

const number = (value, fallback = 0) => {
  const parsed = Number(value)
  return isFinite(parsed) ? parsed : fallback
}

const timestamp = (value, fieldName) => {
  const parsed = Number(value)
  if (!isFinite(parsed) || parsed <= 0) {
    throw new Error(`${fieldName} must be a positive Unix timestamp in milliseconds`)
  }
  return parsed
}

const list = value => {
  if (Array.isArray(value)) return value.slice()
  if (value && typeof value === 'object') return Object.keys(value).map(key => value[key])
  return []
}

const cloneList = value => list(value).map(item => {
  if (!item || typeof item !== 'object') return item
  return {...item}
})

const requiredText = (value, fieldName) => {
  const parsed = text(value).trim()
  if (!parsed) throw new Error(`${fieldName} is required`)
  return parsed
}

const requiredInventoryLot = value => {
  const lot = normalizeInventoryLotName(value)
  if (!lot) throw new Error('inventoryLot is required')
  return lot
}

const requiredInventoryTerm = value => {
  const inventoryTerm = text(value).trim()
  if (!isInventoryTerm(inventoryTerm)) {
    throw new Error('inventoryTerm must be either longTerm or shortTerm')
  }
  return inventoryTerm
}

const getDealerId = (dealer, explicitId) => {
  return text(explicitId || (dealer && (dealer['.key'] || dealer.key || dealer.uid || dealer.id))).trim()
}

const coordinatePair = coordinate => {
  if (Array.isArray(coordinate) && coordinate.length >= 2) {
    return [number(coordinate[0], NaN), number(coordinate[1], NaN)]
  }
  if (coordinate && typeof coordinate === 'object') {
    return [
      number(coordinate.lng !== undefined ? coordinate.lng : coordinate.longitude, NaN),
      number(coordinate.lat !== undefined ? coordinate.lat : coordinate.latitude, NaN)
    ]
  }
  return [NaN, NaN]
}

const isPointOnSegment = (point, first, second) => {
  const crossProduct = (point[1] - first[1]) * (second[0] - first[0]) -
    (point[0] - first[0]) * (second[1] - first[1])
  if (Math.abs(crossProduct) > 0.0000001) return false

  const dotProduct = (point[0] - first[0]) * (second[0] - first[0]) +
    (point[1] - first[1]) * (second[1] - first[1])
  if (dotProduct < 0) return false

  const squaredLength = Math.pow(second[0] - first[0], 2) + Math.pow(second[1] - first[1], 2)
  return dotProduct <= squaredLength
}

const isPointInPolygon = (point, coordinates) => {
  const polygon = list(coordinates)
    .map(coordinatePair)
    .filter(pair => isFinite(pair[0]) && isFinite(pair[1]))

  if (polygon.length < 3) return false

  let inside = false
  for (let currentIndex = 0, previousIndex = polygon.length - 1; currentIndex < polygon.length; previousIndex = currentIndex++) {
    const current = polygon[currentIndex]
    const previous = polygon[previousIndex]

    if (isPointOnSegment(point, previous, current)) return true

    const intersects = ((current[1] > point[1]) !== (previous[1] > point[1])) &&
      (point[0] < ((previous[0] - current[0]) * (point[1] - current[1])) /
        (previous[1] - current[1]) + current[0])
    if (intersects) inside = !inside
  }
  return inside
}

const normalizeZone = zone => {
  if (!zone) return {id: '', name: ''}
  if (typeof zone === 'string') return {id: '', name: zone}

  return {
    id: text(zone.id || zone['.key'] || zone.zoneId),
    name: text(zone.zoneName || zone.name || zone.zone)
  }
}

export const findInventoryZone = ({lat, long, zones, zoneResolver, dealer}) => {
  const latitude = number(lat, NaN)
  const longitude = number(long, NaN)
  if (typeof zoneResolver === 'function' && isFinite(latitude) && isFinite(longitude)) {
    return normalizeZone(zoneResolver(latitude, longitude, dealer))
  }

  if (isFinite(latitude) && isFinite(longitude) && Array.isArray(zones)) {
    const matchingZone = zones.find(zone => zone && isPointInPolygon([longitude, latitude], zone.coordinates))
    if (matchingZone) return normalizeZone(matchingZone)
  }

  const embeddedZone = dealer && dealer.zone && typeof dealer.zone === 'object'
    ? dealer.zone
    : {
      id: dealer && (dealer.zoneId || dealer.payerZoneId),
      name: dealer && (dealer.zoneName || dealer.zone || dealer.payerZone)
    }
  return normalizeZone(embeddedZone)
}

const inventoryBaseOrder = ({dateEntered, creationMethod, orderDetails, transporter}) => {
  const enteredAt = timestamp(dateEntered, 'dateEntered')
  const details = orderDetails || {}
  const company = transporter || {}

  return {
    isInventory: true,
    inventoryCreationMethod: creationMethod,
    dateEntered: enteredAt,
    creationDate: enteredAt,
    status: 'Processing',
    nbDispatch: 0,
    isPrinted: false,
    isBilling: false,
    source: text(details.source || 'Inventory'),
    numRef: text(details.numRef),
    comment: text(details.comment),
    docs: '',
    datePromis: '',
    dateExpected: '',
    dateSignataire: '',
    signataire: '',
    warningTime: 0,
    lateTime: 0,
    initialLateTime: 0,
    billNum: '',
    latitude: 0,
    longitude: 0,
    transpCigle: text(company.cigle || company.transpCigle),
    transpName: text(company.name || company.transpName),
    vin: '',
    make: '',
    model: '',
    trim: '',
    year: '',
    size: 1,
    weight: 0,
    realWeight: 0,
    photos: [],
    bodyPhotos: [],
    regularPrice: 0,
    price: 0,
    discount: 0,
    discountAmountType: '%',
    surplus: 0,
    surplusAmountType: '%',
    surplusByVehicleClass: 0,
    surplusRebateNote: '',
    fuelCharge: 0,
    applyFuelCharge: false,
    includedFuelCharge: true,
    fees: [],
    selectedContactEmails: [],
    transmitterEmailList: [],
    receiverEmailList: [],
    distance: 0,
    deliveryType: 'delivery'
  }
}

export const mapDealerToInventoryOrderFields = ({dealer, dealerId, payerUID, zones, zoneResolver}) => {
  const account = dealer || {}
  const accountId = getDealerId(account, dealerId || payerUID)
  const rawLatitude = account.lat !== undefined ? account.lat : account.latitude
  const rawLongitude = account.long !== undefined ? account.long : account.longitude
  const latitude = number(rawLatitude, 0)
  const longitude = number(rawLongitude, 0)
  const zone = findInventoryZone({
    lat: rawLatitude,
    long: rawLongitude,
    zones,
    zoneResolver,
    dealer: account
  })
  const contacts = cloneList(account.contacts || account.payerContact)
  const email = text(account.email || account.receptionEmail)
  const name = text(account.name || account.payerName)
  const address = text(account.address || account.payerAddress)
  const city = text(account.city || account.payerCity)
  const postcode = text(account.postcode || account.payerPostcode)
  const province = text(account.province || account.payerProvince)
  const country = text(account.country || account.payerCountry)
  const phone = text(account.phone || account.payerPhone)
  const fax = text(account.fax || account.payerFax)
  const placeId = text(account.placeId || account.place_id)
  const contact = text(account.contact)
  const emailList = list(account.emailList)

  return {
    payer: accountId,
    payerName: name,
    payerAddress: address,
    payerCity: city,
    payerPostcode: postcode,
    payerProvince: province,
    payerCountry: country,
    payerPhone: phone,
    payerEmail: email,
    payerFax: fax,
    payerContact: contacts,
    payerZone: zone.name,
    payerZoneId: zone.id,

    transmitter: accountId,
    transmitterName: name,
    transmitterAddress: address,
    transmitterCity: city,
    transmitterPostcode: postcode,
    transmitterProvince: province,
    transmitterCountry: country,
    transmitterPhone: phone,
    transmitterEmail: email,
    transmitterFax: fax,
    transmitterContact: contact,
    transmitterContacts: cloneList(contacts),
    transmitterEmailList: emailList.slice(),
    transmitterLat: latitude,
    transmitterLong: longitude,
    transmitterPlaceId: placeId,
    transmitterZone: zone.name,
    transmitterZoneId: zone.id,

    receiver: accountId,
    receiverName: name,
    receiverAddress: address,
    receiverCity: city,
    receiverPostcode: postcode,
    receiverProvince: province,
    receiverCountry: country,
    receiverPhone: phone,
    receiverEmail: email,
    receiverFax: fax,
    receiverContact: contact,
    receiverContacts: cloneList(contacts),
    receiverEmailList: emailList.slice(),
    receiverLat: latitude,
    receiverLong: longitude,
    receiverPlaceId: placeId,
    receiverZone: zone.name,
    receiverZoneId: zone.id,

    latitude,
    longitude,
    sageID: text(account.sageID),
    payerOdooPartnerId: text(account.payerOdooPartnerId || account.odooPartnerId),
    payerOdooPartnerName: text(account.payerOdooPartnerName || account.odooPartnerName),
    zeroTax: account.zeroTax === true,
    billingComment: text(account.billingComment)
  }
}

const mapVehicleFields = (vehicle, photos = []) => {
  const car = vehicle || {}
  return {
    vin: text(car.vin).trim(),
    make: text(car.make),
    model: text(car.model),
    trim: text(car.trim),
    year: text(car.year),
    size: car.size === '' || car.size === null || typeof car.size === 'undefined' ? 1 : number(car.size, 1),
    weight: number(car.weight, 0),
    realWeight: number(car.realWeight, 0),
    photos: list(photos),
    bodyPhotos: []
  }
}

const sourcePayerAsDealer = sourceOrder => ({
  uid: sourceOrder.payer,
  name: sourceOrder.payerName,
  address: sourceOrder.payerAddress,
  city: sourceOrder.payerCity,
  postcode: sourceOrder.payerPostcode,
  province: sourceOrder.payerProvince,
  country: sourceOrder.payerCountry,
  phone: sourceOrder.payerPhone,
  email: sourceOrder.payerEmail,
  fax: sourceOrder.payerFax,
  contacts: sourceOrder.payerContact,
  lat: sourceOrder.payerLat,
  long: sourceOrder.payerLong,
  placeId: sourceOrder.payerPlaceId,
  zone: sourceOrder.payerZone,
  zoneId: sourceOrder.payerZoneId,
  sageID: sourceOrder.sageID,
  odooPartnerId: sourceOrder.payerOdooPartnerId,
  odooPartnerName: sourceOrder.payerOdooPartnerName,
  zeroTax: sourceOrder.zeroTax,
  billingComment: sourceOrder.billingComment
})

export const buildManualInventoryOrder = options => {
  const config = options || {}
  const dealer = config.dealer || {}
  const vehicle = config.vehicle || {}
  const dropOffPerson = requiredText(config.dropOffPerson, 'dropOffPerson')
  const inventoryLot = requiredInventoryLot(config.inventoryLot || vehicle.inventoryLot)
  const dealerFields = mapDealerToInventoryOrderFields(config)
  if (!dealerFields.payer) throw new Error('dealerId is required')
  if (!text(vehicle.vin).trim()) throw new Error('vehicle.vin is required')

  const dropOffOrganization = requiredText(
    config.dropOffOrganization || dealer.name || dealerFields.payerName,
    'dropOffOrganization'
  )
  const order = {
    ...inventoryBaseOrder({
      dateEntered: config.dateEntered,
      creationMethod: 'manual',
      orderDetails: config.orderDetails,
      transporter: config.transporter
    }),
    ...dealerFields,
    ...mapVehicleFields(vehicle),
    inventoryLot,
    inventoryTerm: INVENTORY_TERM.LONG_TERM,
    inventoryDropOffPerson: dropOffPerson,
    inventoryDropOffOrganization: dropOffOrganization
  }

  return order
}

export const buildInventoryOrderFromSource = options => {
  const config = options || {}
  const sourceOrder = config.sourceOrder || {}
  if (!sourceOrder || typeof sourceOrder !== 'object' || isInventoryOrder(sourceOrder)) {
    throw new Error('sourceOrder must be a regular delivery')
  }
  const sourceOrderId = requiredText(
    config.sourceOrderId || sourceOrder['.key'] || sourceOrder.orderNumber,
    'sourceOrderId'
  )
  const inventoryLot = requiredInventoryLot(config.inventoryLot)
  const inventoryTerm = requiredInventoryTerm(config.inventoryTerm)
  if (sourceOrder.sentToInventory === true) throw new Error(`Order ${sourceOrderId} was already sent to inventory`)

  const dealer = config.dealer || sourcePayerAsDealer(sourceOrder)
  const dealerId = config.dealerId || config.payerUID || sourceOrder.payer
  const dealerFields = mapDealerToInventoryOrderFields({
    dealer,
    dealerId,
    zones: config.zones,
    zoneResolver: config.zoneResolver
  })
  const transporter = config.transporter || {
    cigle: sourceOrder.transpCigle,
    name: sourceOrder.transpName
  }

  return {
    ...inventoryBaseOrder({
      dateEntered: config.dateEntered,
      creationMethod: 'from_order',
      orderDetails: {source: 'Inventory', numRef: sourceOrderId, comment: ''},
      transporter
    }),
    ...dealerFields,
    ...mapVehicleFields(sourceOrder, getLatestDropPhotos(sourceOrder)),
    inventoryLot,
    inventoryTerm,
    sourceOrderId,
    numRef: sourceOrderId
  }
}

export const createInventoryNote = ({date, name, text: noteText}) => ({
  date: timestamp(date, 'note.date'),
  name: text(name),
  text: requiredText(noteText, 'note.text')
})

export const buildInitialInventoryNoteText = order => {
  const lot = text(order && order.inventoryLot).trim()
  const lotSuffix = lot ? ` - Lot ${lot}` : ''
  if (order && order.inventoryCreationMethod === 'from_order') {
    return `INVENTORY CREATED - From order ${text(order.sourceOrderId || order.numRef)}${lotSuffix}`
  }

  const person = text(order && order.inventoryDropOffPerson).trim()
  const organization = text(order && order.inventoryDropOffOrganization).trim()
  if (person && organization) return `INVENTORY ENTERED - Dropped off by ${person} from ${organization}${lotSuffix}`
  if (person) return `INVENTORY ENTERED - Dropped off by ${person}${lotSuffix}`
  return `INVENTORY ENTERED - Manual entry${lotSuffix}`
}

export const buildInventoryArrivalConfirmationNoteText = photoCount => {
  const total = number(photoCount, 0)
  return total > 0
    ? `INVENTORY ARRIVAL CONFIRMED - ${total} drop photos received`
    : 'INVENTORY ARRIVAL CONFIRMED'
}

export const buildInventoryDepartureNoteText = ({requesterName, requesterOrganization, comment}) => {
  const person = requiredText(requesterName, 'requesterName')
  const organization = requiredText(requesterOrganization, 'requesterOrganization')
  const extra = text(comment).trim()
  return `INVENTORY LEAVING - Requested by ${person} from ${organization}${extra ? ` - ${extra}` : ''}`
}

export const buildInventoryBillingNoteText = billingReference => {
  const reference = text(billingReference).trim()
  return `INVENTORY BILLED / DONE${reference ? ` - Reference: ${reference}` : ''}`
}
